import {stegaClean} from '@sanity/client/stega'
import {safeHref} from '@/lib/safe-href'
import type {ActionButton, MegaMenuItem, MenuDocument, MenuItem} from '@/types/sanity'

export const isMenuItemVisible = (item?: MenuItem | null): item is MenuItem =>
  Boolean(item) && item?.hidden !== true

type LinkLikeItem = Pick<
  MenuItem | MegaMenuItem | ActionButton,
  'linkType' | 'externalUrl' | 'customUrl' | 'internalLink' | 'fileLink'
>

/**
 * Applies the shared `safeHref` guard to an editor-typed URL, with one local
 * adaptation: a bare `#` is kept.
 *
 * `safeHref` rejects `#` on the reasoning that it is not a destination, which
 * is right upstream — there `'#'` is the *rejection sentinel* `resolveLink`
 * returns, so a bare `#` never arrives as input. Here `null` is the sentinel
 * and `#` is a placeholder editors type deliberately (and the prototype uses).
 * Passing it through the guard unadapted turns every such button into nothing:
 * `LayoutBlocks.tsx` drops a button whose href is null, rather than falling
 * back to `'#'` the way `ActionButtonRow` and `ImageTextSection` do.
 *
 * `#` is safe by inspection — a same-page anchor cannot navigate off-origin.
 * This adapts jotul's convention to the shared guard; it does not weaken it.
 * Anything else `safeHref` rejects stays rejected.
 */
const resolveEditorHref = (raw?: string): string | null => {
  const cleaned = stegaClean(raw)
  if (cleaned?.trim() === '#') return '#'
  return safeHref(cleaned)
}

export const resolveMenuItemHref = (item: LinkLikeItem): string | null => {
  if (item.linkType === 'none') {
    return null
  }

  // `externalUrl` / `customUrl` are free-text fields an editor types into, and
  // 16 files feed this result straight into `href={...}`. `stegaClean` first:
  // an encoded `/<stega>/evil.com` passes the allow-list uncleaned and becomes
  // `//evil.com` afterwards. `null` is the rejection sentinel every caller
  // already handles.
  if (item.linkType === 'external') {
    return resolveEditorHref(item.externalUrl)
  }

  if (item.linkType === 'custom') {
    return resolveEditorHref(item.customUrl)
  }

  if (item.linkType === 'internal') {
    const slug = item.internalLink?.slug?.current
    return slug ? `/${slug}` : null
  }

  if (item.linkType === 'file') {
    return item.fileLink?.url || null
  }

  return null
}

/**
 * JO2-246: a menu link in the mega menu. "Hide in the mega menu" counts only
 * while the new header layout is on, where the link can still show in the
 * header row; with the layout off a ticked box must not make a link vanish.
 */
export const isMegaMenuItemVisible = (
  item: MenuItem | null | undefined,
  newHeaderLayout: boolean,
): item is MenuItem => isMenuItemVisible(item) && !(newHeaderLayout && item.hideInMegaMenu === true)

export type HeaderMenuLink = {
  key: string
  label: string
  href: string
  openInNewTab?: boolean
  nofollow?: boolean
}

/**
 * JO2-246: the main-menu links an editor ticked "Also show in the header", in
 * menu order (each top-level item, then its children). Hidden items and items
 * without a usable link are left out.
 */
export const headerMenuLinks = (menu: MenuDocument | null | undefined): HeaderMenuLink[] => {
  const out: HeaderMenuLink[] = []
  const add = (item: MenuItem) => {
    if (!isMenuItemVisible(item) || item.showInHeader !== true) return
    const href = resolveMenuItemHref(item)
    const label = stegaClean(item.label)?.trim()
    if (!href || !label) return
    out.push({
      key: item._key,
      label,
      href,
      openInNewTab: item.openInNewTab,
      nofollow: item.nofollow,
    })
  }
  for (const item of menu?.items ?? []) {
    add(item)
    for (const child of item.children ?? []) add(child)
  }
  return out
}
